import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Star } from "lucide-solid";
import type { ShopProduct } from "@famidex/powersync";

import { useI18n } from "@/providers/i18n-provider";
import { AcornGlyph, formatAcorns, ShopIcon } from "./shop-visuals";

/**
 * Tienda de MONEDAS (packs de bellotas por dinero real). Traducción del diseño
 * Stitch `tienda_de_monedas`. La compra real (IAP) va DETRÁS DE FLAG: con
 * `iapEnabled=false` el botón muestra "Próximamente" (sin muro de pago — el resto
 * de la economía es jugable con bellotas gratis). El precio real (price_cents) se
 * pinta como referencia. El importe otorgado (acorns_granted) lo acredita el
 * servidor; el cliente nunca fija bellotas.
 */
const CENTS_PER_UNIT = 100;

/** Formatea un precio en céntimos como divisa (referencia del pack IAP). */
function formatPrice(cents: number | null): string {
  if (cents === null) {
    return "";
  }
  return new Intl.NumberFormat("es", { style: "currency", currency: "USD" }).format(
    cents / CENTS_PER_UNIT,
  );
}

export function CoinShop(props: {
  packs: ShopProduct[];
  iapEnabled: boolean;
  busy: boolean;
  onPurchase: (product: ShopProduct) => void;
}) {
  const { t } = useI18n();

  return (
    <section class="flex flex-col gap-3" data-testid="coin-shop">
      <h2 class="text-sm font-bold uppercase tracking-wide text-muted-foreground">
        {t(msg`Packs de Bellotas`)}
      </h2>
      <ul class="flex flex-col gap-2.5">
        <For each={props.packs}>
          {(pack) => (
            <li
              data-testid="coin-pack"
              class="flex items-center gap-3 rounded-xl border border-border/60 bg-card p-3.5"
            >
              <span class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground">
                <ShopIcon name={pack.icon} class="h-6 w-6" />
              </span>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <h3 class="truncate text-sm font-semibold text-foreground">{pack.name}</h3>
                  <Show when={pack.badge === "best_value"}>
                    <span class="inline-flex items-center gap-0.5 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                      <Star class="h-3 w-3" />
                      {t(msg`Mejor valor`)}
                    </span>
                  </Show>
                </div>
                <p class="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <AcornGlyph class="h-3.5 w-3.5 text-primary" />
                  {formatAcorns(pack.acorns_granted ?? 0)} {t(msg`bellotas`)}
                </p>
              </div>
              <button
                type="button"
                data-testid="buy-pack"
                disabled={!props.iapEnabled || props.busy}
                onClick={() => {
                  props.onPurchase(pack);
                }}
                class="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Show when={props.iapEnabled} fallback={t(msg`Próximamente`)}>
                  {formatPrice(pack.price_cents)}
                </Show>
              </button>
            </li>
          )}
        </For>
      </ul>
    </section>
  );
}
